/**
 * AR Renderer
 * Manages Three.js rendering and video plane display
 */

import * as THREE from 'three';
import { DeviceCapabilityDetector } from '../utils/performance-utils.js';
import { QUALITY_PRESETS } from '../config/performance.config.js';

/**
 * T032: VideoTextureManager for conditional texture updates
 * Tracks video element playback times to avoid unnecessary GPU transfers
 */
export class VideoTextureManager {
  constructor() {
    // Map: videoElement -> last processed currentTime
    this.lastVideoTimes = new Map();
  }

  /**
   * Update texture only if video time has changed
   * @param {THREE.VideoTexture} texture - Video texture to update
   * @param {HTMLVideoElement} videoElement - Source video element
   * @returns {boolean} - True if texture was updated
   */
  updateTexture(texture, videoElement) {
    if (!texture || !videoElement) {
      return false;
    }

    const currentTime = videoElement.currentTime;
    const lastTime = this.lastVideoTimes.get(videoElement);

    // Check if video time has changed
    if (lastTime !== undefined && Math.abs(currentTime - lastTime) < 0.001) {
      // Video hasn't advanced, skip GPU transfer
      return false;
    }

    // Video time changed, update texture
    texture.needsUpdate = true;
    this.lastVideoTimes.set(videoElement, currentTime);

    return true;
  }

  /**
   * Clear tracking for a video element
   * @param {HTMLVideoElement} videoElement - Video element to clear
   */
  clearVideo(videoElement) {
    this.lastVideoTimes.delete(videoElement);
  }

  /**
   * Clear all tracked videos
   */
  clearAll() {
    this.lastVideoTimes.clear();
  }

  /**
   * Get statistics
   * @returns {Object} - Stats object
   */
  getStats() {
    return {
      trackedVideos: this.lastVideoTimes.size,
    };
  }
}

/**
 * AR Renderer class for Three.js scene management
 */
export class ARRenderer {
  constructor() {
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.videoPlane = null;
    this.videoTexture = null;
    this.isInitialized = false;
    this.animationFrameId = null;

    // Device capabilities and quality settings (T004, T005)
    this.deviceCapabilities = null;
    this.qualitySettings = null;
    this.currentQualityTier = null;

    // Video frame synchronization (iOS Safari HLS compatibility)
    this.videoElement = null;
    this.videoFrameCallbackId = null;
    this.lastVideoFrameTime = 0;
  }

  /**
   * Initialize renderer with MindAR's Three.js objects
   * @param {THREE.WebGLRenderer} renderer - MindAR renderer
   * @param {THREE.Scene} scene - MindAR scene
   * @param {THREE.Camera} camera - MindAR camera
   * @param {Object} options - Optional configuration
   * @param {string} options.qualityTier - Force specific quality tier ('high'|'mid'|'low')
   */
  init(renderer, scene, camera, options = {}) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    // T004: Detect device capabilities
    this.detectDeviceCapabilities();

    // T005: Apply quality settings based on device tier or override
    const tier = options.qualityTier || this.deviceCapabilities.gpuTier;
    this.applyQualitySettings(tier);

    // Configure renderer for AR - transparent background to show camera feed
    this.renderer.setClearColor(0x000000, 0); // Transparent background
    this.renderer.setClearAlpha(0);

    // Color space for accurate color reproduction
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // Tone mapping for better HDR-like appearance
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    console.log(
      `✨ Renderer optimized: tier=${this.currentQualityTier}, pixelRatio=${this.renderer.getPixelRatio()}, colorSpace=sRGB`
    );

    // Enable stencil buffer for masking
    const gl = this.renderer.getContext();
    if (gl.getContextAttributes().stencil) {
      console.log('Stencil buffer enabled');
    } else {
      console.warn('Stencil buffer not available');
    }

    this.isInitialized = true;
    console.log('ARRenderer initialized');
  }

  /**
   * T004: Detect device capabilities
   * Detects WebGL version, max anisotropy, texture size, and classifies GPU tier
   */
  detectDeviceCapabilities() {
    const gl = this.renderer ? this.renderer.getContext() : null;
    this.deviceCapabilities = DeviceCapabilityDetector.detect(gl);

    console.log(
      `🔍 Device detected: ${this.deviceCapabilities.platform}, GPU tier: ${this.deviceCapabilities.gpuTier}`
    );
    console.log(
      `   WebGL ${this.deviceCapabilities.webglVersion}, Max texture: ${this.deviceCapabilities.maxTextureSize}, Max anisotropy: ${this.deviceCapabilities.maxAnisotropy}`
    );

    return this.deviceCapabilities;
  }

  /**
   * T005: Apply quality settings based on tier
   * @param {string} tier - Quality tier ('high'|'mid'|'low')
   */
  applyQualitySettings(tier) {
    if (!QUALITY_PRESETS[tier]) {
      console.warn(`Invalid quality tier "${tier}", defaulting to "mid"`);
      tier = 'mid';
    }

    this.qualitySettings = { ...QUALITY_PRESETS[tier] };
    this.currentQualityTier = tier;

    // Apply pixel ratio
    const pixelRatio =
      this.qualitySettings.pixelRatio !== null
        ? this.qualitySettings.pixelRatio
        : window.devicePixelRatio;
    this.renderer.setPixelRatio(pixelRatio);

    console.log(`⚙️ Quality settings applied: ${tier} tier`);
    console.log(
      `   Pixel ratio: ${pixelRatio}, Texture res: ${this.qualitySettings.textureResolution}, Anisotropy: ${this.qualitySettings.anisotropy}`
    );

    return this.qualitySettings;
  }

  /**
   * Create video plane mesh
   * @param {HTMLVideoElement} videoElement - Video element for texture
   * @param {number} width - Plane width
   * @param {number} height - Plane height
   * @returns {THREE.Mesh} - Video plane mesh
   */
  createVideoPlane(videoElement, width = 1, height = 1) {
    if (!this.isInitialized) {
      throw new Error('ARRenderer not initialized');
    }

    // Store video element reference for frame callback
    this.videoElement = videoElement;

    // Create video texture with quality settings
    this.videoTexture = new THREE.VideoTexture(videoElement);

    // iOS Safari compatibility: Disable mipmaps for dynamic video textures
    // Reason: Mipmap generation every frame (60fps) causes GPU overload on iOS Safari
    // Symptoms: Video jitter especially with small/distant markers (uses multiple mipmap levels)
    // Solution: Use LinearFilter without mipmaps for stable video playback
    this.videoTexture.minFilter = THREE.LinearFilter;
    this.videoTexture.magFilter = THREE.LinearFilter;
    this.videoTexture.format = THREE.RGBAFormat;

    // DISABLED: Mipmaps cause iOS Safari video jitter (fd7d5ce regression)
    // this.videoTexture.generateMipmaps = true;
    this.videoTexture.generateMipmaps = false;

    // Anisotropic filtering based on quality settings (T005)
    const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
    const targetAnisotropy = this.qualitySettings ? this.qualitySettings.anisotropy : 8;
    this.videoTexture.anisotropy = Math.min(maxAnisotropy, targetAnisotropy);

    // Color space for accurate color reproduction
    this.videoTexture.colorSpace = THREE.SRGBColorSpace;

    console.log(
      `✨ Video texture optimized: anisotropy=${this.videoTexture.anisotropy}, mipmaps=false (iOS compat), tier=${this.currentQualityTier}`
    );

    // Setup RequestVideoFrameCallback for iOS Safari HLS frame synchronization
    // This ensures texture updates only when actual video frames are rendered
    if ('requestVideoFrameCallback' in videoElement) {
      const videoFrameCallback = (now, metadata) => {
        // Mark that a new video frame is available
        this.lastVideoFrameTime = now;
        this.videoTexture.needsUpdate = true;

        // Schedule next callback
        this.videoFrameCallbackId = videoElement.requestVideoFrameCallback(videoFrameCallback);
      };

      // Start the callback loop
      this.videoFrameCallbackId = videoElement.requestVideoFrameCallback(videoFrameCallback);
      console.log(
        '✅ RequestVideoFrameCallback enabled for precise frame synchronization (iOS HLS)'
      );
    } else {
      console.warn('⚠️ RequestVideoFrameCallback not supported, using fallback method');
    }

    // Create plane geometry
    const geometry = new THREE.PlaneGeometry(width, height);

    // Create material with video texture
    const material = new THREE.MeshBasicMaterial({
      map: this.videoTexture,
      side: THREE.DoubleSide,
      transparent: false,
    });

    // Create mesh
    this.videoPlane = new THREE.Mesh(geometry, material);

    console.log(`Video plane created: ${width}x${height}`);
    return this.videoPlane;
  }

  /**
   * Add object to scene
   * @param {THREE.Object3D} object - Object to add
   */
  addObject(object) {
    if (!this.isInitialized) {
      throw new Error('ARRenderer not initialized');
    }

    this.scene.add(object);
    console.log('Object added to scene');
  }

  /**
   * Remove object from scene
   * @param {THREE.Object3D} object - Object to remove
   */
  removeObject(object) {
    if (!this.isInitialized) {
      return;
    }

    this.scene.remove(object);
    console.log('Object removed from scene');
  }

  /**
   * Setup stencil buffer for masking
   * Creates a black background outside poster area
   */
  setupStencilBuffer() {
    if (!this.isInitialized) {
      throw new Error('ARRenderer not initialized');
    }

    // Note: MindAR handles most of the stencil setup
    // Keep transparent background to show camera feed
    console.log('Stencil buffer configured with transparent background');
  }

  /**
   * Render scene
   */
  render() {
    if (!this.isInitialized) {
      return;
    }

    // iOS Safari HLS frame synchronization:
    // If RequestVideoFrameCallback is available, texture updates are handled by the callback
    // Only fallback to every-frame update if callback is not supported
    if (this.videoTexture && !this.videoFrameCallbackId) {
      // Fallback: Update video texture every frame (older browsers)
      this.videoTexture.needsUpdate = true;
    }
    // Note: When RequestVideoFrameCallback is active, needsUpdate is set in the callback

    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Start render loop
   * @param {Function} updateCallback - Optional callback before each render
   */
  startRenderLoop(updateCallback = null) {
    if (this.animationFrameId) {
      console.warn('Render loop already running');
      return;
    }

    const animate = () => {
      this.animationFrameId = requestAnimationFrame(animate);

      // Call update callback if provided
      if (updateCallback) {
        updateCallback();
      }

      this.render();
    };

    animate();
    console.log('Render loop started');
  }

  /**
   * Stop render loop
   */
  stopRenderLoop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
      console.log('Render loop stopped');
    }
  }

  /**
   * Resize renderer
   * @param {number} width - New width
   * @param {number} height - New height
   */
  resize(width, height) {
    if (!this.isInitialized) {
      return;
    }

    this.renderer.setSize(width, height);
    console.log(`Renderer resized: ${width}x${height}`);
  }

  /**
   * Get video plane mesh
   * @returns {THREE.Mesh|null}
   */
  getVideoPlane() {
    return this.videoPlane;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stopRenderLoop();

    // Cancel video frame callback if active
    if (this.videoFrameCallbackId && this.videoElement) {
      this.videoElement.cancelVideoFrameCallback(this.videoFrameCallbackId);
      this.videoFrameCallbackId = null;
      console.log('Video frame callback cancelled');
    }

    // Dispose video texture
    if (this.videoTexture) {
      this.videoTexture.dispose();
      this.videoTexture = null;
    }

    // Dispose video plane
    if (this.videoPlane) {
      if (this.videoPlane.geometry) {
        this.videoPlane.geometry.dispose();
      }
      if (this.videoPlane.material) {
        if (this.videoPlane.material.map) {
          this.videoPlane.material.map.dispose();
        }
        this.videoPlane.material.dispose();
      }
      this.videoPlane = null;
    }

    this.videoElement = null;
    this.lastVideoFrameTime = 0;
    this.isInitialized = false;
    console.log('ARRenderer cleaned up');
  }
}
